import * as React from 'react';
// mui
import {
  Box,
  List,
  ListItemText,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListSubheader,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
// redux
import { useSelector } from 'react-redux';
import { selectCompleteTasksByParentId, selectTaskById, selectUncompleteTasksByParentId } from '../../store/selectors/taskSelectors';
import { selectCompleteEventsByParentId, selectUncompleteEventsByParentId } from '../../store/selectors/eventSelectors';
import { selectCompleteRecurringTasksByParentId, selectUncompleteRecurringTasksByParentId } from '../../store/selectors/recuringTaskSelectors';
// icons
import AddBoxIcon from '@mui/icons-material/AddBox';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
// types
import RecurringTask from '../../interfaces/RecurringTask.interface';
import Task from '../../interfaces/Task.interface';
import Event from '../../interfaces/Event.interface';
// components
import TaskListRecurringItem from './RecurringTaskItem/RecurringTaskItem';
import RecurringTaskDialog from './RecurringTaskItem/RecurringTaskDialog';
import TaskDialog from './TaskItem/TaskDialog';
import TaskItem from './TaskItem/TaskItem';
import EventItem from './EventItem/EventItem';
import EventDialog from './EventItem/EventDialog';
import MiniTaskList from './TaskItem/MiniTaskList';

interface TaskListProps {
  padding: number;
  parentTaskId: number | null;
}

interface TaskListItem {
  type: 'task' | 'recurring' | 'event';
  data: Task | RecurringTask | Event;
}

export default function TaskList(props: TaskListProps) {
  const inheritedPadding: number = props.padding;
  const localParentTaskId: number | null = props.parentTaskId;

  const theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down('sm'));

  // get tasks from Redux store
  const selectParentTask = localParentTaskId
    ? selectTaskById(localParentTaskId)
    : () => null;
  const parentTaskRaw = useSelector(selectParentTask);
  const localParentTask: Task | null = parentTaskRaw ?? null;

  const uncompleteTasksSelector = React.useMemo(
    () => selectUncompleteTasksByParentId(localParentTaskId),
    [localParentTaskId]
  );
  const localUncompletedTasks = useSelector(uncompleteTasksSelector);
  const completeTasksSelector = React.useMemo(
    () => selectCompleteTasksByParentId(localParentTaskId),
    [localParentTaskId]
  );
  const localCompletedTasks: Task[] = useSelector(completeTasksSelector);

  const uncompletedEventsSelector = React.useMemo(
    () => selectUncompleteEventsByParentId(localParentTaskId),
    [localParentTaskId]
  );
  const localUncompletedEvents: Event[] = useSelector(uncompletedEventsSelector);
  const completedEventsSelector = React.useMemo(
    () => selectCompleteEventsByParentId(localParentTaskId),
    [localParentTaskId]
  );
  const localCompletedEvents: Event[] = useSelector(completedEventsSelector);

  const uncompletedRecurringTasksSelector = React.useMemo(
    () => selectUncompleteRecurringTasksByParentId(localParentTaskId),
    [localParentTaskId]
  );
  const localUncompleteRecurringTasks: RecurringTask[] = useSelector(uncompletedRecurringTasksSelector);
  const completedRecurringTasksSelector = React.useMemo(
    () => selectCompleteRecurringTasksByParentId(localParentTaskId),
    [localParentTaskId]
  );
  const localCompleteRecurringTasks: RecurringTask[] = useSelector(completedRecurringTasksSelector);

  // local state for managing whether completed subtasks are shown, set to true if the parent task is completed
  const [ showCompletedSubtasks, setShowCompletedSubtasks ] = React.useState<boolean>(localParentTask ? localParentTask.completed : false);

  // add task dialog
  const [ addTaskDialogStatus, setAddTaskDialogStatus ] = React.useState(false);

  const openAddDialog = () => setAddTaskDialogStatus(true);
  const closeAddDialog = () => setAddTaskDialogStatus(false);

  // add recurring task dialog
  const [ addRecurringTaskDialogStatus, setAddRecurringTaskDialogStatus ] = React.useState(false);

  const openAddRecurringDialog = () => setAddRecurringTaskDialogStatus(true);
  const closeAddRecurringDialog = () => setAddRecurringTaskDialogStatus(false);

  // add event dialog
  const [ addEventDialogStatus, setAddEventDialogStatus ] = React.useState(false);

  const openEventDialog = () => setAddEventDialogStatus(true);
  const closeEventDialog = () => setAddEventDialogStatus(false);

  const buildTaskLists = () => {
    const uncompleteFlatList: TaskListItem[] = [];
    const completeFlatList: TaskListItem[] = [];

    for (let i = 0; i < localUncompletedTasks.length; i += 1) {
      uncompleteFlatList.push({ type: 'task', data: localUncompletedTasks[i], });
    }

    for (let i = 0; i < localCompletedTasks.length; i += 1) {
      completeFlatList.push({ type: 'task', data: localCompletedTasks[i], });
    }

    for (let i = 0; i < localUncompletedEvents.length; i += 1) {
      uncompleteFlatList.push({ type: 'event', data: localUncompletedEvents[i], });
    }

    for (let i = 0; i < localCompletedEvents.length; i += 1) {
      completeFlatList.push({ type: 'event', data: localCompletedEvents[i], });
    }

    for (let i = 0; i < localUncompleteRecurringTasks.length; i += 1) {
      uncompleteFlatList.push({ type: 'recurring', data: localUncompleteRecurringTasks[i], });
    }

    for (let i = 0; i < localCompleteRecurringTasks.length; i += 1) {
      completeFlatList.push({ type: 'recurring', data: localCompleteRecurringTasks[i], });
    }

    const extractDateFromItem = (item: TaskListItem): string | null => {
      if (item.type === 'task') return (item.data as Task).dueDate;
      if (item.type === 'recurring') return (item.data as RecurringTask).dueDate || null;
      if (item.type === 'event') return (item.data as Event).startTime;
      return null;
    }

    const sortItems = (a: TaskListItem, b: TaskListItem) => {
      let aDueDateString: string | null = extractDateFromItem(a);
      let bDueDateString: string | null = extractDateFromItem(b);
      let aTime = aDueDateString ? new Date(aDueDateString).getTime() : 3000000000000;
      let bTime = bDueDateString ? new Date(bDueDateString).getTime() : 3000000000000;

      if (aTime === bTime) { // if both have the same due date
        const priorityDiff: number = b.data.priority - a.data.priority;
        
        if (priorityDiff === 0) {
          return a.data.title.localeCompare(b.data.title); // sort alphabetically
        } else {
          return priorityDiff; // sort by priority
        }
      } else {
        return aTime - bTime; // sort by due date
      }
    };

    const sortedUncompleteList = [...uncompleteFlatList].sort(sortItems);
    const sortedCompleteList = [...completeFlatList].sort(sortItems);

    return {
      sortedUncompleteList,
      sortedCompleteList,
    }
  }

  // sorted complete and uncomplete item list
  const { sortedUncompleteList, sortedCompleteList } = buildTaskLists();
  const uncompleteTaskListItems = sortedUncompleteList;
  const completeTaskListItems = sortedCompleteList;

  const renderTaskListItems = (item: TaskListItem, i: number) => {
    if (item.type === 'task') {
      // render task
      return (
        <TaskItem
          key={i}
          taskId={item.data.id}
          parentTaskId={localParentTaskId}
          padding={inheritedPadding + 4}
        />
      );
    } else if (item.type === 'recurring') {
      // render recurring
      return (
        <TaskListRecurringItem
          key={i}
          recurringTaskId={item.data.id}
          parentTaskId={localParentTaskId}
          padding={inheritedPadding + 4}
        />
      );
    } else if (item.type === 'event') {
      // render event
      return (
        <EventItem
          key={i}
          eventId={item.data.id}
          parentTaskId={localParentTaskId}
          padding={inheritedPadding + 4}
        />
      );
    }
  };

  return (
    <Box>
      <List
        component="div"
        disablePadding
        subheader={localParentTaskId === null &&
          <ListSubheader component="div" id="nested-list-subheader">
            Tasks
          </ListSubheader>
        }
      >
        {/* add task/event/recurring buttons */}
        { localParentTaskId === null &&
          <Grid container>
            <Grid size={4}>
              <ListItemButton
                onClick={openAddDialog}
                sx={{ paddingLeft: 2, pt: isMobile ? 0 : 'auto' }}
              >
                <ListItemIcon sx={{ minWidth: isMobile ? 30 : 40 }}>
                  <AddIcon />
                </ListItemIcon>
                <ListItemText secondary={isMobile ? 'Task' : `Add Task`} />
              </ListItemButton>
            </Grid>
            <Grid size={4}>
              <ListItemButton
                onClick={openEventDialog}
                sx={{ paddingLeft: 2, pt: isMobile ? 0 : 'auto' }}
              >
                <ListItemIcon sx={{ minWidth: isMobile ? 30 : 40 }}>
                  <AddBoxIcon />
                </ListItemIcon>
                <ListItemText secondary={isMobile ? 'Event' : `Add Event`} />
              </ListItemButton>
            </Grid>
            <Grid size={4}>
              <ListItemButton
                onClick={openAddRecurringDialog}
                sx={{ paddingLeft: 2, pt: isMobile ? 0 : 'auto' }}
              >
                <ListItemIcon sx={{ minWidth: isMobile ? 30 : 40 }}>
                  <AddCircleIcon />
                </ListItemIcon>
                <ListItemText secondary={isMobile ? 'Recurring' : `Add Recurring Task`} />
              </ListItemButton>
            </Grid>
          </Grid>
        }

        {/* rendered list of uncomplete tasks */}
        { uncompleteTaskListItems.map(renderTaskListItems) }

        {/* rendered list of uncomplete mini tasks */}
        { (localParentTaskId !== null) && (
          <MiniTaskList
            parentTaskId={localParentTaskId}
            padding={inheritedPadding + 4}
            isCompletedList={false}
          />
        )}

        {/* show completed tasks dropdown button */}
        { ((localCompletedTasks.length > 0
        || localCompletedEvents.length > 0
        || localCompleteRecurringTasks.length > 0
        || (localParentTask ? localParentTask.miniTasks.filter((miniTask) => miniTask.completed).length > 0 : false))
          && !localParentTask?.completed) &&
          <ListItemButton
            onClick={() => { setShowCompletedSubtasks(!showCompletedSubtasks); }}
            sx={{ paddingLeft: (localParentTaskId
              ? (inheritedPadding + (isMobile ? 3 : 6))
              : 2) }}
          >
            <ListItemIcon sx={{ minWidth: isMobile ? 36 : 40 }}>
              {showCompletedSubtasks ?
                <KeyboardArrowDownIcon />
              :
                <KeyboardArrowRightIcon />
              }
            </ListItemIcon>
            <ListItemText secondary={`Show Completed`} />
          </ListItemButton>
        }

        {/* collapse that contains list of completed tasks */}
        <Collapse in={showCompletedSubtasks} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {/* rendered list of completed tasks */}
            { completeTaskListItems.map(renderTaskListItems) }

            {/* rendered list of completed mini tasks */}
            { (localParentTaskId !== null) && (
              <MiniTaskList
                parentTaskId={localParentTaskId}
                padding={inheritedPadding + 4}
                isCompletedList={true}
              />
            )}
          </List>
        </Collapse>
      </List>

      {/* dialog for adding subtask */}
      { addTaskDialogStatus &&
        <TaskDialog
          onSave={closeAddDialog}
          onCancel={closeAddDialog}
          parentTaskId={localParentTaskId}
          taskId={null}
          addMode={true}
        /> }

      {/* dialog for adding event */}
      { addEventDialogStatus &&
        <EventDialog
          onSave={closeEventDialog}
          onCancel={closeEventDialog}
          parentTaskId={localParentTaskId}
          eventId={null}
          addMode={true}
        /> }

      {/* dialog for adding event */}
      { addRecurringTaskDialogStatus &&
        <RecurringTaskDialog
          onSave={closeAddRecurringDialog}
          onCancel={closeAddRecurringDialog}
          parentTaskId={localParentTaskId}
          recurringTaskId={null}
          addMode={true}
        /> }
    </Box>
  )
}
